import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { ActivatedRoute, Router } from '@angular/router'
import { DOCUMENT } from '@angular/common'
import { Subject, takeUntil } from 'rxjs'

import {
  CloseSessionBody,
  CommitteeSessionDetail,
  CommitteeSessionItemDetail,
  MeetingResult,
  SessionMeetingDetail,
  SessionMinute,
  SessionUser,
  SessionVoteParticipant,
} from '../../data/interfaces/committee-session.interface'
import { MANUAL_DECISION_OPTIONS, MEETING_STATUS_PILL, PillDef, SESSION_KIND_LABEL, SESSION_STATUS_PILL } from '../../data/constants/committee.constants'
import { ApiCommitteeService, Wrapped } from '@data/services/modules/committee/api-committee.service'
import { canVote, fullName, idOf, initials, myVote } from '../../data/utils/committee-tally.util'
import { CommitteeMember } from '@modules/private/lease/data/interfaces/committee.interface'
import { FilePreviewService } from '@shared/components/file-preview/file-preview.service'
import { CommitteeShellService } from '../../data/services/view/committee-shell.service'
import { conExtensionDe, guardarBlobComo } from '@data/utils/descarga-archivo.util'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { hasOpenOverlay } from '../../data/utils/overlay.util'

type Decision = 'Aprobado' | 'Rechazado'

/**
 * Una fila del diálogo de cierre: con qué se cierra cada arrendamiento. `manual` marca las reuniones sin
 * votos con quórum, donde Legal elige el dictamen (las 47 heredadas que se votaron en persona).
 */
export interface CloseRow {
  meetingId: string | null
  folio: string
  cast: number
  total: number
  quorumOk: boolean
  /** Resultado si la reunión ya estaba cerrada (el guion de saneamiento mezcla cerradas con una abierta). */
  closedResult: string | null
  projected: MeetingResult
  manual: boolean
  decision: MeetingResult
}

/** El voto que se está redactando: sobre qué arrendamiento, qué se decidió y con qué nota. */
interface VoteDraft {
  item: CommitteeSessionItemDetail
  meeting: SessionMeetingDetail
  decision: Decision
  memberComments: string
}

/** Escapa lo que va dentro del mensaje HTML del confirm: el folio y el cliente los escribe un usuario. */
function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** La respuesta de una escritura de sesión trae el detalle completo; votar (`Committee`) y la observación no. */
function isSessionDetail(data: unknown): data is CommitteeSessionDetail {
  return !!data && typeof (data as CommitteeSessionDetail)._id === 'string' && Array.isArray((data as CommitteeSessionDetail).items)
}

/**
 * Una sesión del comité: sus votantes, sus actas y la tabla de arrendamientos con el voto por fila.
 *
 * Es el destino del botón del correo («Ir a la sesión»). El votante llega, despliega el `brief` de
 * cada fila y vota ahí mismo; Legal ve el avance por votante, sube el acta UNA vez —queda ligada a
 * todos los arrendamientos con el mismo `_id` (D-5)— y cierra.
 *
 * **Lo derivado llega del backend** (D-11): avance por votante, votos por fila, `quorumOk`, la
 * proyección y el resumen para decidir. Aquí solo se decide `canVote`/`myVote` con el usuario actual.
 *
 * **Cada botón declara el MISMO permiso que exige su endpoint** (D-7): cerrar es `close-meeting`
 * (Legal), votar es `vote` (Admin), observaciones y aviso son `update`, votantes es
 * `update-selected-voters`, actas es `upload-minutes`.
 *
 * Sin `MessageService` propio: lo hereda del cascarón por el inyector del outlet, o cada aviso
 * saldría dos veces (ver `CommitteesPageComponent`).
 */
@Component({
  selector: 'app-session-detail',
  templateUrl: './session-detail.component.html',
  styleUrls: ['./session-detail.component.scss'],
  providers: [ConfirmationService],
})
export class SessionDetailComponent implements OnInit, OnDestroy {
  id = ''
  session: CommitteeSessionDetail | null = null
  loading = true
  error = ''
  /** Alguna escritura en vuelo: los botones que escriben se bloquean para no cruzar peticiones. */
  busy = false
  notifying = false
  uploading = false

  /** Qué filas están desplegadas, por id de reunión. */
  expanded = new Set<string>()

  readonly currentUserId: string
  readonly perms: { vote: boolean; close: boolean; update: boolean; voters: boolean; minutes: boolean }

  // El voto, en su diálogo corto
  voteVisible = false
  voteDraft: VoteDraft | null = null

  // ── Cerrar la sesión ──
  closeVisible = false
  closeRows: CloseRow[] = []
  closeReason = ''
  readonly decisionOptions = MANUAL_DECISION_OPTIONS

  // La observación por arrendamiento, en línea
  editingMeetingId: string | null = null
  editingText = ''

  // Los votantes de la sesión
  votersVisible = false
  votersDraft: string[] = []
  // Opciones ESTABLES para el p-multiSelect (referencia fija). Un getter devolvería un array nuevo en
  // cada change detection y el overlay entraría en bucle (lección de `lease-committee`).
  memberOptions: { label: string; value: string }[] = []

  // El visor de actas
  previewVisible = false
  previewUrl: string | null = null
  previewName: string | null = null
  private previewMinuteRef: SessionMinute | null = null

  readonly kindLabel: Record<string, string> = SESSION_KIND_LABEL
  readonly statusPill: Partial<Record<string, PillDef>> = SESSION_STATUS_PILL
  readonly meetingPill: Partial<Record<string, PillDef>> = MEETING_STATUS_PILL
  readonly riskSourceLabel: Record<string, string> = { calculator: 'Excel de riesgo', riskAnalysis: 'Algoritmo' }
  readonly skeleton = [1, 2, 3]

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiCommitteeService,
    private readonly shell: CommitteeShellService,
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly confirmation: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly filePreview: FilePreviewService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    //! Siempre `String()`: el id del usuario viene del localStorage y los del backend pueden llegar
    //! como ObjectId serializado. Comparar sin normalizar es la fuente clásica de «no puedo votar».
    this.currentUserId = String(this.auth.getUser?.user?.id ?? '')
    this.perms = {
      vote: this.auth.hasModulePermission('committee', 'vote'),
      close: this.auth.hasModulePermission('committee', 'close-meeting'),
      update: this.auth.hasModulePermission('committee', 'update'),
      voters: this.auth.hasModulePermission('committee', 'update-selected-voters'),
      minutes: this.auth.hasModulePermission('committee', 'upload-minutes'),
    }
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      this.id = p.get('id') ?? ''
      this.expanded.clear()
      this.load()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  // ── Derivados de estado ──

  get isOpen(): boolean {
    return this.session?.status === 'open'
  }

  get isVoting(): boolean {
    return this.session?.kind === 'voting'
  }

  /** «Cerrar sesión» es de Legal (`close-meeting`): Admin no lo tiene aunque vote (D-7). */
  get canClose(): boolean {
    return this.perms.close && this.isVoting && this.isOpen
  }

  get canNotify(): boolean {
    return this.perms.update && this.isVoting && this.isOpen
  }

  get canEditVoters(): boolean {
    return this.perms.voters && this.isVoting && this.isOpen
  }

  get canUpload(): boolean {
    return this.perms.minutes
  }

  /** La columna «Mi voto» solo existe para quien vota, y en una votación. */
  get showMyVote(): boolean {
    return this.perms.vote && this.isVoting
  }

  /** Cuántas columnas tiene la tabla: manda el `colspan` de la fila desplegada y de la vacía. */
  get columns(): number {
    return this.showMyVote ? 8 : 7
  }

  /** Cuántos convocados ya votaron TODO. */
  get votersDone(): number {
    return (this.session?.voters ?? []).filter((v) => v.pendingCount === 0).length
  }

  /** Los convocados de la sesión: son los `selectedVoters` de cada reunión, abanicados (D-1). */
  private get voterIds(): string[] {
    return (this.session?.voters ?? []).map((v) => idOf(v.user))
  }

  private get votersById(): Record<string, SessionUser> {
    const map: Record<string, SessionUser> = {}
    for (const v of this.session?.voters ?? []) map[idOf(v.user)] = v.user

    return map
  }

  // ── Por fila ──

  /**
   * Si el usuario actual puede votar ESTA fila, ahora. Cruza permiso, convocatoria, voto y estado.
   * Los convocados salen de la reunión; si vinieran vacíos, de los votantes de la sesión (D-1).
   */
  canVoteOn(item: CommitteeSessionItemDetail): boolean {
    const meeting = item.meeting
    if (!this.session || !meeting) return false
    const selectedVoters = meeting.selectedVoters?.length ? meeting.selectedVoters : this.voterIds

    return canVote({ ...meeting, selectedVoters }, this.session.status, this.currentUserId, this.perms.vote)
  }

  myVoteOn(item: CommitteeSessionItemDetail): { decision: Decision; memberComments?: string | null } | undefined {
    return myVote(item.meeting, this.currentUserId)
  }

  /** La observación de una fila se edita con `update`, mientras la reunión siga abierta (D-6). */
  canEditItem(item: CommitteeSessionItemDetail): boolean {
    return this.perms.update && this.isOpen && item.meeting?.meetingStatus === 'Open'
  }

  voterName(memberId: string | SessionUser): string {
    const id = idOf(memberId)
    const user = this.votersById[id] ?? (typeof memberId === 'object' ? memberId : null)

    return fullName(user) || 'Miembro del comité'
  }

  /** Quién votó: por su id si lo trae; los votos heredados (anteriores al ADR-0013) solo traen `memberName`. */
  participantName(p: SessionVoteParticipant): string {
    if (p.memberId) return this.voterName(p.memberId)

    return p.memberName?.trim() || 'Miembro del comité'
  }

  nameOf(user: SessionUser): string {
    return fullName(user) || 'Miembro del comité'
  }

  initialsOf(user: SessionUser): string {
    return initials(this.nameOf(user))
  }

  isMe(user: SessionUser): boolean {
    return idOf(user) === this.currentUserId
  }

  /** Quién votó qué, para el tooltip de la columna Votos. */
  whoVoted(meeting: SessionMeetingDetail, decision: Decision): string {
    return (meeting.participants ?? [])
      .filter((p) => p.decision === decision)
      .map((p) => this.participantName(p))
      .join(', ')
  }

  /** A quién le falta votar esta fila. */
  whoPending(meeting: SessionMeetingDetail): string {
    const voted = new Set((meeting.participants ?? []).map((p) => idOf(p.memberId)))
    const convened = meeting.selectedVoters?.length ? meeting.selectedVoters.map(idOf) : this.voterIds

    return convened
      .filter((id) => !voted.has(id))
      .map((id) => this.voterName(id))
      .join(', ')
  }

  /** Los comentarios de quienes votaron, para el detalle desplegado. */
  voterComments(meeting: SessionMeetingDetail | null): { name: string; decision: Decision; text: string }[] {
    return (meeting?.participants ?? []).filter((p) => (p.memberComments ?? '').trim()).map((p) => ({ name: this.participantName(p), decision: p.decision, text: p.memberComments as string }))
  }

  /** La llave de la fila: la reunión, o el arrendamiento si la reunión ya no existe. */
  rowKey(item: CommitteeSessionItemDetail): string {
    return item.meeting?._id ?? item.lease._id
  }

  isOpenRow(item: CommitteeSessionItemDetail): boolean {
    return this.expanded.has(this.rowKey(item))
  }

  /** Despliega o pliega una fila. El `brief` ya viene con la sesión: no hay nada que pedir. */
  toggle(item: CommitteeSessionItemDetail): void {
    const key = this.rowKey(item)
    if (this.expanded.has(key)) this.expanded.delete(key)
    else this.expanded.add(key)
  }

  resultLabel(status: string | null | undefined): string {
    return (status && this.meetingPill[status]?.label) || status || '—'
  }

  // ── Votar (Admin miembro) ──

  openVote(item: CommitteeSessionItemDetail, decision: Decision): void {
    if (!item.meeting || !this.canVoteOn(item)) return
    this.voteDraft = { item, meeting: item.meeting, decision, memberComments: '' }
    this.voteVisible = true
  }

  /** Votar sigue siendo por reunión (D-4): el endpoint no cambió. */
  submitVote(): void {
    const draft = this.voteDraft
    if (!draft || this.busy || !draft.item.committee) return

    this.busy = true
    this.api.castCommitteeVote(draft.item.committee._id, draft.meeting._id, { decision: draft.decision, memberComments: draft.memberComments.trim() || undefined }).subscribe((r) => {
      this.afterAction(r, 'Voto emitido.', () => {
        this.voteVisible = false
        this.voteDraft = null
      })
    })
  }

  // ── Observaciones por arrendamiento (D-6) ──

  startEdit(item: CommitteeSessionItemDetail): void {
    if (!item.meeting || !this.canEditItem(item)) return
    this.editingMeetingId = item.meeting._id
    this.editingText = item.meeting.generalComments ?? ''
  }

  cancelEdit(): void {
    this.editingMeetingId = null
    this.editingText = ''
  }

  saveEdit(item: CommitteeSessionItemDetail): void {
    if (this.busy || !item.meeting) return

    this.busy = true
    this.api.updateSessionItem(this.id, item.meeting._id, { generalComments: this.editingText.trim() }).subscribe((r) => {
      this.afterAction(r, 'Observación guardada.', () => this.cancelEdit())
    })
  }

  // ── Cerrar (Legal) ──

  /**
   * El diálogo lista, POR arrendamiento, con qué se cierra: folio, votos, quórum y resultado. Donde los
   * votos deciden se enseña la proyección; donde NO hay votos con quórum —las reuniones heredadas que se
   * votaron en persona y solo tienen el acta— Legal elige el dictamen y escribe el motivo, que va a la
   * reunión y a la bitácora de cada arrendamiento. Con votos y quórum el backend rechaza cualquier dictamen.
   */
  confirmClose(): void {
    if (!this.session || !this.canClose) return
    const total = this.session.voters.length
    this.closeRows = this.session.items.map((it) => this.toCloseRow(it, total))
    this.closeReason = ''
    this.closeVisible = true
  }

  private toCloseRow(it: CommitteeSessionItemDetail, total: number): CloseRow {
    const m = it.meeting
    const cast = m ? m.votes.approved + m.votes.rejected : 0
    const open = m?.meetingStatus === 'Open'
    const projected: MeetingResult = m?.projected ?? 'Revision'
    return {
      meetingId: m?._id ?? null,
      folio: it.lease.folio ?? '—',
      cast,
      total,
      quorumOk: !!m?.quorumOk,
      closedResult: m && !open ? m.meetingStatus : null,
      projected,
      manual: !!m && open && (cast === 0 || !m.quorumOk),
      decision: projected,
    }
  }

  /** Hay algún dictamen manual: el motivo es obligatorio. */
  get closeNeedsReason(): boolean {
    return this.closeRows.some((r) => r.manual)
  }

  get canSubmitClose(): boolean {
    return !this.busy && (!this.closeNeedsReason || this.closeReason.trim().length >= 10)
  }

  /** Mismo motivo que en «Nueva sesión» (QA-11): Escape con el desplegable del dictamen abierto solo cierra el desplegable. */
  onCloseEscape(event: Event): void {
    if (this.busy || hasOpenOverlay(this.document)) return
    event.preventDefault()
    this.closeVisible = false
  }

  submitClose(): void {
    if (!this.canSubmitClose) return
    this.busy = true
    const manual = this.closeRows.filter((r) => r.manual && r.meetingId)
    const body: CloseSessionBody = manual.length ? { decisions: manual.map((r) => ({ meetingId: r.meetingId as string, decision: r.decision })), reason: this.closeReason.trim() } : {}
    this.api.closeSession(this.id, body).subscribe((r) => this.afterAction(r, 'Sesión cerrada.', () => (this.closeVisible = false)))
  }

  // ── Reenviar aviso ──

  notify(): void {
    if (this.notifying) return
    this.notifying = true
    this.api.notifySession(this.id).subscribe((r) => {
      this.notifying = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se envió', detail: r.msg })
        return
      }
      //* El backend no aborta si SendGrid rechaza un correo: lo reporta por destinatario. Se dice aquí.
      const recipients = r.data?.recipients ?? []
      const failed = recipients.filter((x) => !x.emailSent).length
      const sent = recipients.length - failed
      if (failed > 0) {
        this.messageService.add({ severity: 'warn', summary: 'Aviso parcial', detail: `Se avisó a ${sent}; no se pudo enviar a ${failed}.`, life: 8000 })
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Aviso enviado',
          detail: sent ? `Se reenvió el correo a ${sent} ${sent === 1 ? 'votante' : 'votantes'} con votos pendientes.` : 'Nadie tiene votos pendientes.',
        })
      }
      this.load()
    })
  }

  // ── Votantes ──

  openVoters(): void {
    if (!this.canEditVoters) return
    this.votersDraft = this.voterIds
    this.votersVisible = true
    if (this.memberOptions.length) return

    this.api.getCommitteeMembers().subscribe((r) => {
      const list = (r.data ?? []) as CommitteeMember[]
      this.memberOptions = list.map((m) => ({ label: fullName(m), value: String(m._id) }))
    })
  }

  /** Mismo motivo que en «Nueva sesión» (QA-11): Escape con el desplegable de votantes abierto solo cierra el desplegable. */
  onVotersEscape(event: Event): void {
    if (this.busy || hasOpenOverlay(this.document)) return
    event.preventDefault()
    this.votersVisible = false
  }

  saveVoters(): void {
    if (this.busy || !this.votersDraft.length) return
    this.busy = true
    this.api.updateSessionVoters(this.id, { selectedVoters: this.votersDraft }).subscribe((r) => {
      this.afterAction(r, 'Votantes actualizados.', () => (this.votersVisible = false))
    })
  }

  // ── Actas ──

  /** `app-input-single-file` ya subió el archivo; aquí solo se registra el acta contra la sesión. */
  onMinuteSelected(url: string): void {
    if (!url || this.uploading) return
    this.uploading = true
    this.api.addSessionMinute(this.id, { file: url }).subscribe((r) => {
      this.uploading = false
      this.afterAction(r, 'Acta agregada. Quedó ligada a todos los arrendamientos de la sesión.', () => undefined)
    })
  }

  confirmRemoveMinute(minute: SessionMinute): void {
    this.confirmation.confirm({
      key: 'acta',
      header: 'Quitar acta',
      message: `Se quitará «${escapeHtml(minute.name)}» de la sesión y de sus ${this.session?.itemsCount ?? 0} arrendamientos. El archivo permanece en el expediente.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-sm p-button-danger',
      rejectButtonStyleClass: 'p-button-sm p-button-text',
      accept: () => {
        this.busy = true
        this.api.removeSessionMinute(this.id, minute._id).subscribe((r) => this.afterAction(r, 'Acta quitada.', () => undefined))
      },
    })
  }

  previewMinute(minute: SessionMinute): void {
    if (!minute?.file) return
    this.previewMinuteRef = minute
    this.previewUrl = minute.file
    this.previewName = minute.name?.trim() || 'acta_comite'
    this.previewVisible = true
  }

  /** Descarga desde el visor: baja LA que el visor tiene abierta, con su nombre (lección B-036). */
  downloadPreview(): void {
    if (this.previewMinuteRef) this.downloadMinute(this.previewMinuteRef)
  }

  /** Abre también un archivo del `brief` (dictamen, presentación) en el mismo visor. */
  previewFile(url: string | null, name: string): void {
    if (!url) return
    this.previewMinuteRef = { _id: '', file: url, name }
    this.previewUrl = url
    this.previewName = name
    this.previewVisible = true
  }

  downloadMinute(minute: SessionMinute): void {
    const url = minute?.file
    if (!url) return
    this.filePreview.fetchBlob(url).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'El archivo está vacío.' })
          return
        }
        guardarBlobComo(blob, conExtensionDe(minute.name?.trim() || 'acta_comite', url))
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo descargar el acta.' }),
    })
  }

  goBack(): void {
    this.router.navigate(['/comites/sesiones'])
  }

  reload(): void {
    this.load()
  }

  /**
   * Cierra el ciclo de una escritura: si falló se avisa y NO se recarga —la pantalla se queda como
   * estaba, con lo que el usuario escribió—; si salió bien se avisa, se pinta el detalle que la propia
   * escritura devolvió (o se recarga, si no lo trae) y se le dice al cascarón que las cifras cambiaron.
   */
  private afterAction(r: Wrapped<unknown>, successMsg: string, onSuccess: () => void): void {
    this.busy = false
    if (r?.error) {
      this.messageService.add({ severity: 'error', summary: 'No se pudo', detail: r.msg || 'Ocurrió un error.' })
      return
    }
    onSuccess()
    this.messageService.add({ severity: 'success', summary: 'Listo', detail: successMsg })
    //* Las escrituras de sesión ya devuelven el detalle recalculado: se pinta y no se vuelve a pedir.
    //* Votar devuelve el `Committee` y la observación devuelve la nota: esas dos sí recargan.
    if (isSessionDetail(r.data)) this.session = r.data
    else this.load()
    this.shell.refresh()
  }

  private load(): void {
    if (!this.id) return
    this.loading = true
    this.error = ''
    this.api.getSession(this.id).subscribe((r) => {
      this.loading = false
      if (r.error || !r.data) {
        this.session = null
        this.error = r.msg || 'No se pudo cargar la sesión.'
        return
      }
      this.session = r.data
    })
  }
}
